import { AvatarAndCreatedAt } from '@/components/avatar-and-createdat';
import { EditQuestionDialog } from '@/components/edit-question-dialog';
import { QuestionActions } from '@/components/question-actions';
import { QuestionCategory } from '@/components/question-category';
import { RichTextContentViewer } from '@/components/rich-text-viewer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCreateQuestion } from '@/hooks/useCreateQuestion';
import { errorHandler } from '@/lib/errorHandler';
import { cn } from '@/lib/utils';
import { ApiErrorResponse, SlugRoutes } from '@/types/api/common';
import { QuestionInfoType } from '@/types/api/idea-starter';
import { Trash } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { toast } from 'sonner';

interface QuestionCardProps {
  question: QuestionInfoType;
  hideUserInfo?: boolean;
  hideReport?: boolean;
  slugRoute?: SlugRoutes;
  hideAddStoryAction?: boolean;
  isActivity?: boolean;
}

export const QuestionCard = ({
  question,
  hideUserInfo,
  hideReport,
  slugRoute = 'home',
  hideAddStoryAction,
  isActivity,
}: QuestionCardProps) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEditQuestion = () => {
    setShowEditDialog(true);
  };

  const handleDeleteQuestion = (id: string) => {
    setShowDeleteDialog(true);
  };

  const { deleteQuestionApi } = useCreateQuestion();

  const handleDeleteQuestionConfirmation = () => {
    setIsDeleting(true);
    deleteQuestionApi.mutate(
      { id: question.id },
      {
        onSuccess: () => {
          toast.success('Question deleted successfully');
          setShowDeleteDialog(false);
        },
        onError: (error: ApiErrorResponse) => {
          toast.error(errorHandler(error));
        },
        onSettled: () => {
          setIsDeleting(false);
        },
      }
    );
  };

  return (
    <>
      <Link href={`/${slugRoute}/question/${question.slug}`}>
        <Card
          className={cn(
            'shadow-md rounded-lg p-4',
            hideAddStoryAction &&
              'shadow-none border-0 border-b border-b-gray-200 py-6 rounded-none hover:bg-gray-50 transition-colors duration-200',
            isDeleting && 'opacity-50'
          )}
        >
          <CardContent className="px-0">
            <div className="flex flex-col relative gap-4">
              {!hideUserInfo && (
                <AvatarAndCreatedAt
                  user={question.author}
                  createdDate={question.createdDate}
                  isAnonymous={question.isAnonymous}
                />
              )}

              <RichTextContentViewer title={question.questionTitle} />

              {/* Show full description (including links) for all questions */}
              {question.questionDescription && (
                <div className="mt-2">
                  <RichTextContentViewer
                    description={question.questionDescription}
                  />
                </div>
              )}

              <QuestionCategory
                questionInfo={question}
                slugRoute={slugRoute}
                hideAddStoryAction={hideAddStoryAction}
              />

              {!hideReport && (
                <QuestionActions
                  questionId={question.id}
                  questionAuthorId={question.author?.id ?? ''}
                  isActivity={isActivity}
                  onHandleEditQuestion={handleEditQuestion}
                  onHandleDeleteQuestion={handleDeleteQuestion}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </Link>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-bold">
              Delete question?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-between">
            <AlertDialogCancel
              className={cn(
                buttonVariants({ variant: 'outline', size: 'lg' }),
                'uppercase font-bold flex-1 w-full text-black hover:bg-black/10 hover:text-black border-black'
              )}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                buttonVariants({ variant: 'destructive', size: 'lg' }),
                'uppercase font-bold flex-1 w-full'
              )}
              onClick={handleDeleteQuestionConfirmation}
              disabled={isDeleting}
            >
              <Trash className="w-4 h-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditQuestionDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        questionInfo={question}
      />
    </>
  );
};
